/**
 * Competency Profile Editor (Sprint TCO-A).
 *
 * Edits: name, department, description, competencies (catalog + custom),
 *        linked questionnaires (PUBLISHED Evaluation Library only).
 * Lifecycle actions: submit / approve / publish / archive / send-back.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  ArrowLeft, Save, Plus, Trash2, Send, CheckCircle2, Sparkles,
  Archive as ArchiveIcon, RotateCcw, Library, BookOpen,
} from 'lucide-react';

import { Topbar } from '@/components/layout/Topbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';

import { useApp } from '@/context/AppContext';
import { competencyProfileApi } from '@/lib/competencyProfileApi';
import { adminApi } from '@/lib/adminApi';
import { formatApiError } from '@/lib/api';

const STATUS_TONES = {
  draft:     'bg-slate-100 text-slate-700 border-slate-200',
  submitted: 'bg-amber-50 text-amber-700 border-amber-200',
  approved:  'bg-blue-50 text-blue-700 border-blue-200',
  published: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  archived:  'bg-zinc-100 text-zinc-500 border-zinc-200',
};

const LEVELS = [1, 2, 3, 4, 5];

// Founder UAT iter 4 — objective (target level) scale guidance.
const LEVEL_HELP = {
  1: { es: 'Básico — conoce los fundamentos; requiere supervisión constante.', en: 'Basic — knows fundamentals; needs constant supervision.' },
  2: { es: 'En desarrollo — aplica la competencia en situaciones simples con apoyo.', en: 'Developing — applies it in simple situations with support.' },
  3: { es: 'Competente — se desempeña de forma autónoma en el día a día.', en: 'Competent — performs autonomously day to day.' },
  4: { es: 'Avanzado — domina situaciones complejas y guía a otros.', en: 'Advanced — handles complex situations and coaches others.' },
  5: { es: 'Experto — referente organizacional; define estándares y estrategia.', en: 'Expert — organizational reference; sets standards and strategy.' },
};

// Founder UAT iter 4 — explicit 6-step workflow guidance.
function currentStep(profile, form) {
  if (!profile) return 1;
  if (profile.status === 'published') return 6;
  if (profile.status === 'approved') return 5;
  if (profile.status === 'submitted') return 4;
  if ((form.linked_questionnaire_ids || []).length > 0) return 4;
  if ((form.competencies || []).length > 0) return 3;
  return 2;
}

// Role-aware actions available per current status.
function actionsFor(role, status) {
  const isAdmin = role === 'hm_admin';
  const isAdvisor = role === 'hm_advisor';
  const out = [];
  if (status === 'draft') {
    // Founder UAT post-168 (C) — HM Admin IS the approval authority: no
    // meaningless "send to myself for review". Advisors submit to HM Admin.
    if (isAdvisor)            out.push({ to: 'submitted', label: 'Submit for HM Admin review', variant: 'default', icon: Send, testid: 'submit' });
    if (isAdmin)              out.push({ to: 'approved',  label: 'Approve (HM authority)', variant: 'default', icon: CheckCircle2, testid: 'approve' });
    if (isAdmin || isAdvisor) out.push({ to: 'archived',  label: 'Archive',            variant: 'ghost',    icon: ArchiveIcon,  testid: 'archive' });
  } else if (status === 'submitted') {
    if (isAdmin) out.push({ to: 'approved', label: 'Approve',         variant: 'default',  icon: CheckCircle2, testid: 'approve' });
    if (isAdmin) out.push({ to: 'draft',    label: 'Request changes', variant: 'outline',  icon: RotateCcw,    testid: 'request-changes', requireNote: true });
    if (isAdmin) out.push({ to: 'archived', label: 'Archive',         variant: 'ghost',    icon: ArchiveIcon,  testid: 'archive' });
  } else if (status === 'approved') {
    if (isAdmin) out.push({ to: 'published', label: 'Publish',         variant: 'default',  icon: Sparkles,     testid: 'publish' });
    if (isAdmin) out.push({ to: 'draft',     label: 'Back to draft',   variant: 'outline',  icon: RotateCcw,    testid: 'back-to-draft' });
    if (isAdmin) out.push({ to: 'archived',  label: 'Archive',         variant: 'ghost',    icon: ArchiveIcon,  testid: 'archive' });
  } else if (status === 'published') {
    if (isAdmin) out.push({ to: 'archived', label: 'Archive', variant: 'ghost', icon: ArchiveIcon, testid: 'archive' });
  } else if (status === 'archived') {
    if (isAdmin) out.push({ to: 'draft', label: 'Restore as draft', variant: 'outline', icon: RotateCcw, testid: 'restore' });
  }
  return out;
}

// Resolve who performed a lifecycle step from the profile's history trail.
function historyName(profile, toStatus) {
  const hits = (profile.history || []).filter((h) => h.to_status === toStatus);
  return hits.length ? (hits[hits.length - 1].by_user_name || null) : null;
}

function GovStamp({ label, value, testid }) {
  return (
    <div data-testid={testid}>
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={`font-medium ${value ? '' : 'text-muted-foreground'}`}>{value || '—'}</p>
    </div>
  );
}

export default function CompetencyProfileEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lvlLang = (i18n.language || 'es').startsWith('es') ? 'es' : 'en';
  const { user } = useApp();
  const role = user?.role;

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: '', department: '', description: '', competencies: [], linked_questionnaire_ids: [] });
  const [catalogComps, setCatalogComps] = useState([]);
  const [catalogQs, setCatalogQs] = useState([]);
  const [addCatalogOpen, setAddCatalogOpen] = useState(false);
  const [addCustomOpen, setAddCustomOpen] = useState(false);
  const [customDraft, setCustomDraft] = useState({ name: '', target_level: 3, notes: '' });
  const [pendingTransition, setPendingTransition] = useState(null); // {to, label, requireNote}
  const [transitionNote, setTransitionNote] = useState('');
  const [acting, setActing] = useState(false);
  const [advisors, setAdvisors] = useState([]);

  useEffect(() => {
    if (role !== 'hm_admin') return;
    adminApi.listConsultants().then(setAdvisors).catch(() => {});
  }, [role]);

  const handleAssignAdvisor = async (v) => {
    try {
      const updated = await competencyProfileApi.update(id, { assigned_advisor_id: v === 'none' ? '' : v });
      setProfile(updated);
      toast.success(t('tco.gov.advisorSaved', { defaultValue: 'Responsible advisor updated' }));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const reload = async () => {
    setLoading(true);
    try {
      const [p, comps, qs] = await Promise.all([
        competencyProfileApi.get(id),
        competencyProfileApi.catalogCompetencies(),
        competencyProfileApi.catalogQuestionnaires(),
      ]);
      setProfile(p);
      setForm({
        name: p.name || '',
        department: p.department || '',
        description: p.description || '',
        competencies: p.competencies || [],
        linked_questionnaire_ids: p.linked_questionnaire_ids || [],
      });
      setCatalogComps(comps);
      setCatalogQs(qs);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  const canEdit = useMemo(() => {
    if (!profile) return false;
    if (profile.status === 'published' || profile.status === 'archived') return false;
    if (profile.status === 'approved' && role !== 'hm_admin') return false;
    return role === 'hm_admin' || role === 'hm_advisor';
  }, [profile, role]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await competencyProfileApi.update(id, {
        name: form.name,
        department: form.department || null,
        description: form.description || null,
        competencies: form.competencies,
        linked_questionnaire_ids: form.linked_questionnaire_ids,
      });
      setProfile(updated);
      toast.success(t('tco.saved', { defaultValue: 'Profile saved' }));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const addCompetencyFromCatalog = (c) => {
    if (form.competencies.some((x) => x.code === c.code)) {
      toast.warning(t('tco.alreadyAdded', { defaultValue: 'Already added' }));
      return;
    }
    setForm((f) => ({
      ...f,
      competencies: [...f.competencies, {
        code: c.code, name: c.name, target_level: 3, source: 'catalog', notes: null,
      }],
    }));
  };

  const addCustomCompetency = () => {
    const name = (customDraft.name || '').trim();
    if (!name) {
      toast.error(t('tco.errors.compNameRequired', { defaultValue: 'Competency name is required.' }));
      return;
    }
    const code = `custom:${name.toLowerCase().replace(/\s+/g, '_').slice(0, 60)}`;
    if (form.competencies.some((x) => x.code === code)) {
      toast.warning(t('tco.alreadyAdded', { defaultValue: 'Already added' }));
      return;
    }
    setForm((f) => ({
      ...f,
      competencies: [...f.competencies, {
        code,
        name,
        target_level: Number(customDraft.target_level) || 3,
        source: 'custom',
        notes: customDraft.notes.trim() || null,
      }],
    }));
    setAddCustomOpen(false);
    setCustomDraft({ name: '', target_level: 3, notes: '' });
  };

  const updateCompetencyLevel = (code, level) => {
    setForm((f) => ({
      ...f,
      competencies: f.competencies.map((c) => c.code === code ? { ...c, target_level: Number(level) } : c),
    }));
  };

  const removeCompetency = (code) => {
    setForm((f) => ({ ...f, competencies: f.competencies.filter((c) => c.code !== code) }));
  };

  const toggleQuestionnaire = (qid) => {
    setForm((f) => {
      const has = f.linked_questionnaire_ids.includes(qid);
      return {
        ...f,
        linked_questionnaire_ids: has
          ? f.linked_questionnaire_ids.filter((x) => x !== qid)
          : [...f.linked_questionnaire_ids, qid],
      };
    });
  };

  const runTransition = async () => {
    if (!pendingTransition) return;
    setActing(true);
    try {
      // Persist any in-flight edits first so the transition operates on saved state.
      const updated = await competencyProfileApi.update(id, {
        name: form.name,
        department: form.department || null,
        description: form.description || null,
        competencies: form.competencies,
        linked_questionnaire_ids: form.linked_questionnaire_ids,
      });
      setProfile(updated);
      const { profile: refreshed } = await competencyProfileApi.transition(
        id, pendingTransition.to, transitionNote.trim() || null,
      );
      setProfile(refreshed);
      toast.success(t('tco.transitioned', {
        defaultValue: 'Status updated to {{status}}',
        status: pendingTransition.to,
      }));
      setPendingTransition(null);
      setTransitionNote('');
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setActing(false);
    }
  };

  if (loading) {
    return (
      <>
        <Topbar title="…" />
        <div className="p-8 text-sm text-muted-foreground" data-testid="tco-editor-loading">
          {t('common.loading', { defaultValue: 'Loading…' })}
        </div>
      </>
    );
  }
  if (!profile) {
    return (
      <>
        <Topbar title={t('tco.notFound', { defaultValue: 'Profile not found' })} />
        <div className="p-8" data-testid="tco-editor-notfound">
          <Button variant="outline" onClick={() => navigate('/admin/talentcompetency')}>
            <ArrowLeft className="h-4 w-4 mr-1.5" />
            {t('tco.backToCatalog', { defaultValue: 'Back to catalog' })}
          </Button>
        </div>
      </>
    );
  }

  const availableActions = actionsFor(role, profile.status);
  const qById = new Map(catalogQs.map((q) => [q.id, q]));

  return (
    <>
      <Topbar
        title={profile.name}
        subtitle={t('tco.editorSubtitle', { defaultValue: 'Competency Profile · {{department}}', department: profile.department || '—' })}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className={STATUS_TONES[profile.status]} data-testid="tco-editor-status">
              {t(`tco.status.${profile.status}`, { defaultValue: profile.status })}
            </Badge>
            <Button variant="outline" onClick={() => navigate('/admin/talentcompetency')} data-testid="tco-editor-back-btn">
              <ArrowLeft className="h-4 w-4 mr-1.5" />
              {t('tco.backToCatalog', { defaultValue: 'Back' })}
            </Button>
            {canEdit && (
              <Button onClick={handleSave} disabled={saving} data-testid="tco-editor-save-btn">
                <Save className="h-4 w-4 mr-1.5" />
                {saving ? t('common.saving', { defaultValue: 'Saving…' }) : t('common.save', { defaultValue: 'Save' })}
              </Button>
            )}
          </div>
        }
      />

      <div className="p-6 lg:p-8 space-y-6" data-testid="tco-editor">
        {/* Founder UAT iter 4 — terminology: what am I building? */}
        <Card className="p-4 bg-primary/5 border-primary/20" data-testid="tco-editor-terminology">
          <p className="text-sm font-semibold">
            {t('tco.editor.terminologyTitle', { defaultValue: 'Estás editando un PERFIL DE COMPETENCIAS para el puesto «{{name}}».', name: profile.name })}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {t('tco.editor.terminologyBody', {
              defaultValue: 'Competencia = una habilidad individual con nivel objetivo (1–5). Perfil de competencias = el conjunto de competencias exigidas por un puesto + las evaluaciones que las miden. Perfil de puesto (TalentMap) = la descripción organizacional del cargo; es un objeto distinto.',
            })}
          </p>
        </Card>

        {/* Founder UAT iter 4 — 6-step workflow stepper */}
        <Card className="p-4" data-testid="tco-editor-stepper">
          <ol className="flex flex-wrap items-center gap-x-1 gap-y-2 text-xs">
            {[
              t('tco.editor.step1', { defaultValue: '1. Crear perfil' }),
              t('tco.editor.step2', { defaultValue: '2. Añadir competencias' }),
              t('tco.editor.step3', { defaultValue: '3. Vincular evaluaciones publicadas' }),
              t('tco.editor.step4', { defaultValue: '4. Enviar a revisión' }),
              t('tco.editor.step5', { defaultValue: '5. Aprobar' }),
              t('tco.editor.step6', { defaultValue: '6. Publicar' }),
            ].map((label, i) => {
              const n = i + 1;
              const step = currentStep(profile, form);
              const done = n < step || profile.status === 'published';
              const active = n === step && profile.status !== 'published';
              return (
                <li key={n} className="flex items-center" data-testid={`tco-editor-step-${n}`}>
                  <span className={`px-2.5 py-1 rounded-full font-medium whitespace-nowrap
                    ${done ? 'bg-emerald-100 text-emerald-700' : active ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
                    {done ? '✓ ' : ''}{label}
                  </span>
                  {n < 6 && <span className="mx-1 text-muted-foreground">→</span>}
                </li>
              );
            })}
          </ol>
        </Card>

        {/* Founder UAT iter 4 — published: guide to the evaluation flow */}
        {profile.status === 'published' && (
          <Card className="p-4 bg-emerald-50/50 border-emerald-200" data-testid="tco-editor-published-next">
            <p className="text-sm font-semibold text-emerald-900">
              {t('tco.editor.publishedNextTitle', { defaultValue: 'Perfil publicado — ¿qué sigue?' })}
            </p>
            <p className="text-xs text-emerald-900/80 mt-1">
              {t('tco.editor.publishedNextBody', {
                defaultValue: 'Flujo de evaluación: el Client Admin solicita una evaluación con este perfil (o llega una solicitud a “Solicitudes de competencias”) → tú asignas → el colaborador responde las evaluaciones vinculadas → se genera la comparación de competencias y el reporte. El perfil es el estándar; las evaluaciones son el instrumento de medición.',
              })}
            </p>
          </Card>
        )}

        {/* Lifecycle actions */}
        {availableActions.length > 0 && (
          <Card className="p-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-sm text-muted-foreground max-w-xl">
                {role === 'hm_admin'
                  ? t('tco.lifecycleHintAdmin', { defaultValue: 'Como HM Admin eres la autoridad de gobernanza: puedes aprobar y publicar directamente, o delegar la elaboración a un asesor responsable.' })
                  : t('tco.lifecycleHintAdvisor', { defaultValue: 'Elabora el perfil y envíalo a revisión de HM Admin. La aprobación y publicación son decisiones de HM Admin.' })}
              </div>
              <div className="flex items-center gap-2 flex-wrap" data-testid="tco-editor-actions">
                {availableActions.map((a) => {
                  const Icon = a.icon;
                  return (
                    <Button
                      key={a.testid}
                      variant={a.variant}
                      size="sm"
                      onClick={() => { setTransitionNote(''); setPendingTransition(a); }}
                      data-testid={`tco-action-${a.testid}`}
                    >
                      <Icon className="h-4 w-4 mr-1.5" />
                      {t(`tco.actions.${a.testid}`, { defaultValue: a.label })}
                    </Button>
                  );
                })}
              </div>
            </div>
          </Card>
        )}

        {/* Founder UAT post-168 (C) — governance & responsibility panel */}
        <Card className="p-4" data-testid="tco-governance-panel">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-2 text-sm">
              <GovStamp label={t('tco.gov.createdBy', { defaultValue: 'Created by' })}
                        value={profile.created_by_name} testid="tco-gov-created" />
              <GovStamp label={t('tco.gov.submittedBy', { defaultValue: 'Submitted by' })}
                        value={historyName(profile, 'submitted')
                          || (historyName(profile, 'approved')
                            ? t('tco.gov.directApproval', { defaultValue: 'No requerido — aprobación directa CTH' })
                            : null)}
                        testid="tco-gov-submitted" />
              <GovStamp label={t('tco.gov.approvedBy', { defaultValue: 'Approved by' })}
                        value={historyName(profile, 'approved')} testid="tco-gov-approved" />
              <GovStamp label={t('tco.gov.publishedBy', { defaultValue: 'Published by' })}
                        value={historyName(profile, 'published')} testid="tco-gov-published" />
            </div>
            <div className="min-w-[220px]">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                {t('tco.gov.assignedAdvisor', { defaultValue: 'Responsible advisor' })}
              </Label>
              {role === 'hm_admin' ? (
                <Select
                  value={profile.assigned_advisor_id || 'none'}
                  onValueChange={handleAssignAdvisor}
                >
                  <SelectTrigger className="h-8 mt-1 text-xs" data-testid="tco-gov-advisor-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('tco.gov.noAdvisor', { defaultValue: 'Not assigned' })}</SelectItem>
                    {advisors.map((a) => (
                      <SelectItem key={a.id} value={a.id}>{a.name || a.email}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-sm font-medium mt-1" data-testid="tco-gov-advisor-readonly">
                  {profile.assigned_advisor_name || t('tco.gov.noAdvisor', { defaultValue: 'Not assigned' })}
                </p>
              )}
            </div>
          </div>
        </Card>

        {/* Founder UAT sprint (FIX 6/7) — configuration readiness of linked assessments */}
        {(profile.linked_assessments_readiness || []).length > 0 && (
          <Card className="p-4" data-testid="tco-readiness-panel">
            <p className="text-sm font-semibold">
              {t('tco.readiness.title', { defaultValue: 'Disponibilidad operativa de evaluaciones vinculadas' })}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {t('tco.readiness.concept', {
                defaultValue: 'Evaluación = instrumento · Configuración = despliegue operativo (idioma, nivel, versión, plantilla de informe, compatibilidad de suite) · Perfil = referencia de competencias del puesto · La asignación siempre se ejecuta sobre una configuración ACTIVA.',
              })}
            </p>
            <div className="mt-3 space-y-2">
              {profile.linked_assessments_readiness.map((r) => (
                <div key={r.id} className="flex items-center justify-between gap-3 text-sm border rounded-md px-3 py-2" data-testid={`tco-readiness-row-${r.id}`}>
                  <div className="min-w-0">
                    <p className="font-medium truncate">{r.name}</p>
                    <p className="text-xs text-muted-foreground font-mono">{r.code}</p>
                  </div>
                  {r.suite_ready ? (
                    <Badge className="bg-success/10 text-success border-success/30 shrink-0" data-testid={`tco-readiness-ok-${r.id}`}>
                      {t('tco.readiness.ready', { defaultValue: 'Operativa — configuración activa' })}
                    </Badge>
                  ) : r.configs_total > 0 ? (
                    <Badge className="bg-warning/10 text-warning border-warning/30 shrink-0" data-testid={`tco-readiness-draft-${r.id}`}>
                      {t('tco.readiness.noActiveForSuite', { defaultValue: 'Sin configuración ACTIVA compatible — no asignable' })}
                    </Badge>
                  ) : (
                    <Badge className="bg-destructive/10 text-destructive border-destructive/30 shrink-0" data-testid={`tco-readiness-none-${r.id}`}>
                      {t('tco.readiness.noConfig', { defaultValue: 'Sin configuración — no asignable' })}
                    </Badge>
                  )}
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Metadata */}
        <Card className="p-5 space-y-4">
          <h2 className="text-lg font-semibold">{t('tco.section.metadata', { defaultValue: 'Profile details' })}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="tco-name">{t('tco.fields.name', { defaultValue: 'Name' })} <span className="text-destructive">*</span></Label>
              <Input
                id="tco-name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                disabled={!canEdit}
                data-testid="tco-editor-name-input"
              />
            </div>
            <div>
              <Label htmlFor="tco-dept">{t('tco.fields.department', { defaultValue: 'Department' })}</Label>
              <Input
                id="tco-dept"
                value={form.department}
                onChange={(e) => setForm((f) => ({ ...f, department: e.target.value }))}
                disabled={!canEdit}
                data-testid="tco-editor-dept-input"
              />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="tco-desc">{t('tco.fields.description', { defaultValue: 'Description' })}</Label>
              <Textarea
                id="tco-desc"
                rows={3}
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                disabled={!canEdit}
                data-testid="tco-editor-desc-input"
              />
            </div>
          </div>
        </Card>

        {/* Competencies */}
        <Card className="p-5 space-y-4" data-testid="tco-editor-competencies-card">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h2 className="text-lg font-semibold">{t('tco.section.competencies', { defaultValue: 'Competencies' })}</h2>
              <p className="text-xs text-muted-foreground mt-1">
                {t('tco.competenciesHintV2', {
                  defaultValue: 'Usa el CATÁLOGO para competencias estándar reutilizables entre puestos (liderazgo, comunicación…) y PERSONALIZADAS para habilidades específicas del cargo (normativas, técnicas). Ambas pueden coexistir en el mismo perfil.',
                })}
              </p>
              <p className="text-xs text-muted-foreground mt-1.5" data-testid="tco-editor-level-legend">
                <b>{t('tco.levelLegendTitle', { defaultValue: 'Nivel objetivo (1–5)' })}:</b>{' '}
                {t('tco.levelLegendBody', {
                  defaultValue: '1 Básico · 2 En desarrollo · 3 Competente · 4 Avanzado · 5 Experto. Es el estándar del puesto: en la evaluación, el resultado del colaborador se compara contra este nivel para detectar brechas.',
                })}
              </p>
            </div>
            {canEdit && (
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setAddCatalogOpen(true)} data-testid="tco-editor-add-catalog-btn">
                  <Library className="h-4 w-4 mr-1.5" />
                  {t('tco.addFromCatalog', { defaultValue: 'Add from catalog' })}
                </Button>
                <Button variant="outline" size="sm" onClick={() => setAddCustomOpen(true)} data-testid="tco-editor-add-custom-btn">
                  <Plus className="h-4 w-4 mr-1.5" />
                  {t('tco.addCustom', { defaultValue: 'Add custom' })}
                </Button>
              </div>
            )}
          </div>

          {form.competencies.length === 0 ? (
            <p className="text-sm text-muted-foreground italic" data-testid="tco-editor-competencies-empty">
              {t('tco.noCompetenciesYet', { defaultValue: 'No competencies yet. Add at least one to submit for approval.' })}
            </p>
          ) : (
            <div className="divide-y border rounded-md" data-testid="tco-editor-competencies-list">
              {form.competencies.map((c) => (
                <div key={c.code} className="flex items-center gap-3 px-4 py-3" data-testid={`tco-editor-comp-row-${c.code}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{c.name}</span>
                      <Badge variant="outline" className="text-xs">
                        {c.source === 'custom' ? t('tco.source.custom', { defaultValue: 'custom' }) : t('tco.source.catalog', { defaultValue: 'catalog' })}
                      </Badge>
                    </div>
                    {c.notes && <p className="text-xs text-muted-foreground mt-1">{c.notes}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    <Label htmlFor={`tco-level-${c.code}`} className="text-xs text-muted-foreground whitespace-nowrap">
                      {t('tco.targetLevel', { defaultValue: 'Target' })}
                    </Label>
                    <Select
                      value={String(c.target_level)}
                      onValueChange={(v) => updateCompetencyLevel(c.code, v)}
                      disabled={!canEdit}
                    >
                      <SelectTrigger id={`tco-level-${c.code}`} className="w-20 h-8" data-testid={`tco-editor-comp-level-${c.code}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {LEVELS.map((l) => (
                          <SelectItem key={l} value={String(l)} title={LEVEL_HELP[l][lvlLang]} data-testid={`tco-editor-comp-level-${c.code}-opt-${l}`}>
                            {l} — {LEVEL_HELP[l][lvlLang].split(' — ')[0]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {canEdit && (
                      <Button
                        size="icon" variant="ghost"
                        onClick={() => removeCompetency(c.code)}
                        data-testid={`tco-editor-comp-remove-${c.code}`}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Linked Questionnaires */}
        <Card className="p-5 space-y-4" data-testid="tco-editor-questionnaires-card">
          <div>
            <h2 className="text-lg font-semibold">{t('tco.section.linkedAssessments', { defaultValue: 'Linked assessments' })}</h2>
            <p className="text-xs text-muted-foreground mt-1">
              {t('tco.linkedAssessmentsHintV2', {
                defaultValue: 'Vincula las evaluaciones TalentCompetency que medirán este perfil. Puedes vincular borradores mientras trabajas, pero el perfil solo podrá PUBLICARSE cuando todas las evaluaciones vinculadas estén publicadas (mínimo una).',
              })}
            </p>
          </div>
          {catalogQs.length === 0 ? (
            <div className="p-4 border rounded-md bg-muted/40 text-sm text-muted-foreground" data-testid="tco-editor-questionnaires-empty">
              <BookOpen className="h-4 w-4 inline mr-1.5" />
              {t('tco.noPublishedQuestionnaires', {
                defaultValue: 'No published TalentCompetency questionnaires found in the Evaluation Library yet.',
              })}
            </div>
          ) : (
            <div className="border rounded-md divide-y" data-testid="tco-editor-questionnaires-list">
              {catalogQs.map((q) => {
                const checked = form.linked_questionnaire_ids.includes(q.id);
                return (
                  <label
                    key={q.id}
                    className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-accent/30"
                    data-testid={`tco-editor-q-row-${q.id}`}
                  >
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => canEdit && toggleQuestionnaire(q.id)}
                      disabled={!canEdit}
                      data-testid={`tco-editor-q-check-${q.id}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium">{q.name}</span>
                        {q.code && <code className="text-xs text-muted-foreground">{q.code}</code>}
                        {q.level && <Badge variant="outline" className="text-xs">{q.level}</Badge>}
                        <Badge variant="outline" className={`text-xs ${q.status === 'published' ? 'text-emerald-600 border-emerald-300' : 'text-amber-600 border-amber-300'}`}>
                          {t(`tco.qStatus.${q.status}`, { defaultValue: q.status })}
                        </Badge>
                      </div>
                      {q.description && (
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{q.description}</p>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
          )}
          <div className="text-xs text-muted-foreground" data-testid="tco-editor-linked-count">
            {t('tco.linkedSelected', {
              defaultValue: '{{n}} assessments selected',
              n: form.linked_questionnaire_ids.length,
            })}
          </div>
        </Card>

        {/* History */}
        {(profile.history || []).length > 0 && (
          <Card className="p-5 space-y-3" data-testid="tco-editor-history-card">
            <h2 className="text-lg font-semibold">{t('tco.section.history', { defaultValue: 'Lifecycle history' })}</h2>
            <ul className="space-y-2">
              {profile.history.map((h, idx) => (
                <li key={idx} className="text-sm flex items-start gap-2" data-testid={`tco-editor-history-row-${idx}`}>
                  <Badge variant="outline" className="text-xs">{h.from_status} → {h.to_status}</Badge>
                  <span className="text-muted-foreground">
                    {h.by_user_name || h.by_user_id} ({h.by_role}) · {h.at}
                  </span>
                  {h.note && <span className="italic">— {h.note}</span>}
                </li>
              ))}
            </ul>
          </Card>
        )}
      </div>

      {/* Add from catalog dialog */}
      <Dialog open={addCatalogOpen} onOpenChange={setAddCatalogOpen}>
        <DialogContent className="max-w-2xl" data-testid="tco-add-catalog-dialog">
          <DialogHeader>
            <DialogTitle>{t('tco.addFromCatalogTitle', { defaultValue: 'Pick competencies' })}</DialogTitle>
            <DialogDescription>
              {t('tco.addFromCatalogDesc', {
                defaultValue: 'Selecting a competency adds it with target level 3 — you can change the target after adding.',
              })}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-96 overflow-y-auto divide-y border rounded-md">
            {catalogComps.length === 0 ? (
              <div className="p-4 text-sm text-muted-foreground">
                {t('tco.catalogEmpty', { defaultValue: 'No catalog competencies available.' })}
              </div>
            ) : catalogComps.map((c) => {
              const already = form.competencies.some((x) => x.code === c.code);
              return (
                <button
                  key={c.code}
                  type="button"
                  disabled={already}
                  onClick={() => addCompetencyFromCatalog(c)}
                  className="w-full text-left px-4 py-3 hover:bg-accent/30 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid={`tco-catalog-pick-${c.code}`}
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{c.name}</span>
                    <code className="text-xs text-muted-foreground">{c.code}</code>
                    {c.category && <Badge variant="outline" className="text-xs">{c.category}</Badge>}
                    {already && <Badge className="text-xs">{t('tco.added', { defaultValue: 'added' })}</Badge>}
                  </div>
                  {c.description && <p className="text-xs text-muted-foreground mt-1">{c.description}</p>}
                </button>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddCatalogOpen(false)} data-testid="tco-catalog-close-btn">
              {t('common.done', { defaultValue: 'Done' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Custom competency dialog */}
      <Dialog open={addCustomOpen} onOpenChange={setAddCustomOpen}>
        <DialogContent data-testid="tco-add-custom-dialog">
          <DialogHeader>
            <DialogTitle>{t('tco.addCustomTitle', { defaultValue: 'Add custom competency' })}</DialogTitle>
            <DialogDescription>
              {t('tco.addCustomDesc', {
                defaultValue: 'Use this for role-specific skills not in the global catalog (e.g. IFRS, OSHA, chemical process safety).',
              })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="tco-custom-name">{t('tco.fields.competencyName', { defaultValue: 'Competency name' })} <span className="text-destructive">*</span></Label>
              <Input
                id="tco-custom-name"
                value={customDraft.name}
                onChange={(e) => setCustomDraft((d) => ({ ...d, name: e.target.value }))}
                placeholder="e.g. IFRS reporting"
                data-testid="tco-custom-name-input"
              />
            </div>
            <div>
              <Label htmlFor="tco-custom-level">{t('tco.targetLevel', { defaultValue: 'Target level' })} (1–5)</Label>
              <Select
                value={String(customDraft.target_level)}
                onValueChange={(v) => setCustomDraft((d) => ({ ...d, target_level: Number(v) }))}
              >
                <SelectTrigger id="tco-custom-level" data-testid="tco-custom-level-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LEVELS.map((l) => (
                    <SelectItem key={l} value={String(l)} data-testid={`tco-custom-level-opt-${l}`}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="tco-custom-notes">{t('tco.fields.notes', { defaultValue: 'Notes' })}</Label>
              <Textarea
                id="tco-custom-notes"
                rows={2}
                value={customDraft.notes}
                onChange={(e) => setCustomDraft((d) => ({ ...d, notes: e.target.value }))}
                data-testid="tco-custom-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddCustomOpen(false)} data-testid="tco-custom-cancel-btn">
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button onClick={addCustomCompetency} data-testid="tco-custom-add-btn">
              <Plus className="h-4 w-4 mr-1.5" />
              {t('tco.add', { defaultValue: 'Add' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transition confirmation dialog */}
      <Dialog open={!!pendingTransition} onOpenChange={(o) => { if (!o) setPendingTransition(null); }}>
        <DialogContent data-testid="tco-transition-dialog">
          <DialogHeader>
            <DialogTitle>
              {pendingTransition ? t(`tco.actions.${pendingTransition.testid}`, { defaultValue: pendingTransition.label }) : ''}
            </DialogTitle>
            <DialogDescription>
              {t('tco.transitionWarn', {
                defaultValue: 'This will save the current edits and change the profile status.',
              })}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label htmlFor="tco-note">
              {t('tco.note', { defaultValue: 'Note' })}
              {pendingTransition?.requireNote ? <span className="text-destructive"> *</span> : null}
            </Label>
            <Textarea
              id="tco-note"
              rows={3}
              value={transitionNote}
              onChange={(e) => setTransitionNote(e.target.value)}
              placeholder={t('tco.notePh', { defaultValue: 'Optional comment for the lifecycle history.' })}
              data-testid="tco-transition-note-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingTransition(null)} data-testid="tco-transition-cancel-btn">
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button
              onClick={runTransition}
              disabled={acting || (pendingTransition?.requireNote && !transitionNote.trim())}
              data-testid="tco-transition-confirm-btn"
            >
              {acting ? t('common.working', { defaultValue: 'Working…' }) : t('common.confirm', { defaultValue: 'Confirm' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
